import { isAfter } from "date-fns"
import { CalendarIcon, Copy, Download, Plus, UserX, ZoomIn, ZoomOut } from "lucide-react"

import { BetaTag } from "@/components/ui/beta-tag"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { formatDayMonthYear } from "@/lib/dateFormats"
import { COVER_BETA_NOTE, type coverBadge } from "@/lib/shiftCover"
import {
  DEFAULT_HOUR_WIDTH,
  MAX_HOUR_WIDTH,
  MIN_HOUR_WIDTH,
  ZOOM_STEP,
} from "@/lib/timelineScale"
import { toneClasses } from "@/lib/tone"
import { cn } from "@/lib/utils"

import RouteFilters from "../RouteFilters"

// Not route statuses, but this is where people change what the board shows. Stand-by routes show
// unless hidden.
const EXTRA_STATUS_OPTIONS = [
  { value: "empty_leg", label: "Empty legs" },
  { value: "hide_standby", label: "Stand-by (hide)" },
]

const toggle = (values: string[], value: string) =>
  values.includes(value) ? values.filter((v) => v !== value) : [...values, value]

interface TimelineToolbarProps {
  searchTerm: string
  onSearchChange: (term: string) => void
  statusFilter: string[]
  onStatusFilterChange: (update: (prev: string[]) => string[]) => void
  depots: string[]
  depotFilter: string[]
  onDepotFilterChange: (update: (prev: string[]) => string[]) => void
  sortBy: string
  onSortChange: (sortBy: string) => void
  selectedDate: Date
  onDateChange: (date: Date) => void
  hourWidth: number
  onHourWidthChange: (update: (prev: number) => number) => void
  showShiftPanel: boolean
  onToggleShiftPanel: () => void
  shiftCoverBadge: ReturnType<typeof coverBadge>
  isPastDate: boolean
  canSeeShiftCover: boolean
  canEditRoutes: boolean
  onExport: () => void
  onCreateAdHocRoute: () => void
  onDuplicateRoute: () => void
}

export default function TimelineToolbar({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  depots,
  depotFilter,
  onDepotFilterChange,
  sortBy,
  onSortChange,
  selectedDate,
  onDateChange,
  hourWidth,
  onHourWidthChange,
  showShiftPanel,
  onToggleShiftPanel,
  shiftCoverBadge,
  isPastDate,
  canSeeShiftCover,
  canEditRoutes,
  onExport,
  onCreateAdHocRoute,
  onDuplicateRoute,
}: TimelineToolbarProps) {
  const handleZoomIn = () => {
    onHourWidthChange((prev) => Math.min(prev + ZOOM_STEP, MAX_HOUR_WIDTH))
  }

  const handleZoomOut = () => {
    onHourWidthChange((prev) => Math.max(prev - ZOOM_STEP, MIN_HOUR_WIDTH))
  }

  return (
    <div className="flex-shrink-0 border-b border-palette-jungleGreen200 bg-white">
      <div className="flex">
        <div className="w-64 flex-shrink-0 border-r border-palette-jungleGreen200">
          <RouteFilters
            className="p-2"
            searchTerm={searchTerm}
            onSearchChange={onSearchChange}
            extraStatusOptions={EXTRA_STATUS_OPTIONS}
            statusFilter={statusFilter}
            onStatusToggle={(status) => onStatusFilterChange((prev) => toggle(prev, status))}
            depots={depots}
            depotFilter={depotFilter}
            onDepotToggle={(depot) => onDepotFilterChange((prev) => toggle(prev, depot))}
            sortBy={sortBy}
            onSortChange={onSortChange}
          />
        </div>

        <div className="flex flex-1 items-center justify-between px-3">
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 border-palette-jungleGreen200 bg-white text-xs text-palette-jungleGreen700 hover:bg-palette-jungleGreen000"
                >
                  <CalendarIcon size={12} className="mr-1.5" />
                  {formatDayMonthYear(selectedDate)}
                </Button>
              </PopoverTrigger>
              <PopoverContent variant="plain" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && onDateChange(date)}
                  disabled={(date) => isAfter(date, new Date())}
                  className="bg-white"
                />
              </PopoverContent>
            </Popover>

            <div className="flex items-center gap-0.5 rounded-md border border-palette-jungleGreen200 bg-white">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleZoomOut}
                disabled={hourWidth <= MIN_HOUR_WIDTH}
                className="h-7 px-1.5 text-palette-jungleGreen600 hover:bg-palette-jungleGreen100"
              >
                <ZoomOut size={12} />
              </Button>
              <span className="min-w-[36px] px-1.5 text-center text-xs text-palette-jungleGreen500">
                {Math.round((hourWidth / DEFAULT_HOUR_WIDTH) * 100)}%
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleZoomIn}
                disabled={hourWidth >= MAX_HOUR_WIDTH}
                className="h-7 px-1.5 text-palette-jungleGreen600 hover:bg-palette-jungleGreen100"
              >
                <ZoomIn size={14} />
              </Button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {canSeeShiftCover && (
              <Button
                variant={showShiftPanel ? "default" : "outline"}
                size="sm"
                onClick={onToggleShiftPanel}
                className={cn(
                  "relative h-7 gap-1.5 text-xs",
                  showShiftPanel
                    ? "bg-blue-600 text-white hover:bg-blue-700"
                    : "border-palette-jungleGreen200 bg-white text-palette-jungleGreen600 hover:bg-palette-jungleGreen000"
                )}
              >
                <UserX size={12} />
                Cover
                <BetaTag title={COVER_BETA_NOTE} />
                {shiftCoverBadge && (
                  <span
                    className={cn(
                      "absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full text-[10px] font-medium",
                      showShiftPanel
                        ? "bg-white text-blue-600"
                        : cn(toneClasses(shiftCoverBadge.tone).dot, "text-white")
                    )}
                  >
                    {shiftCoverBadge.count > 99 ? "99+" : shiftCoverBadge.count}
                  </span>
                )}
              </Button>
            )}
            {isPastDate ? (
              <Button
                variant="outline"
                size="sm"
                onClick={onExport}
                className="h-7 gap-1.5 border-palette-jungleGreen200 bg-white text-xs text-palette-jungleGreen600 hover:bg-palette-jungleGreen000"
              >
                <Download size={12} />
                Export
              </Button>
            ) : canEditRoutes ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onCreateAdHocRoute}
                  className="h-7 gap-1.5 border-palette-jungleGreen200 bg-white text-xs text-palette-jungleGreen600 hover:bg-palette-jungleGreen000"
                >
                  <Plus size={12} />
                  Ad-hoc Route
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onDuplicateRoute}
                  className="h-7 gap-1.5 border-palette-jungleGreen200 bg-white text-xs text-palette-jungleGreen600 hover:bg-palette-jungleGreen000"
                >
                  <Copy size={12} />
                  Duplicate Route
                </Button>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
